import React from "react";
import { Facebook, Instagram, MessageCircle } from "lucide-react";
import { TikTokIcon } from "./icons/TikTokIcon";
import { footer } from "../content/landingContent";
import { useResourceImage } from "../lib/useResourceImage";
import { useContentField, useLinks } from "../lib/useContent";
import { trackContact } from "../lib/metaPixel";
import logo from "../assets/brand/logo-header.png";

const colClass =
  "pt-6 border-t border-white/10 sm:pt-0 sm:border-t-0 lg:pl-8 lg:border-l lg:border-white/10";

export const Footer: React.FC = () => {
  const { src: logoSrc } = useResourceImage("header.logo", logo);
  const links = useLinks();
  const domain = useContentField("footer.domain", footer.domain);
  const domainUrl = useContentField("footer.domainUrl", footer.domainUrl);
  const facebookHandle = useContentField("footer.facebookHandle", footer.facebookHandle);
  const instagramHandle = useContentField("footer.instagramHandle", footer.instagramHandle);
  const tiktokHandle = useContentField("footer.tiktokHandle", footer.tiktokHandle);
  const whatsappNumber = useContentField("footer.whatsappNumber", footer.whatsappNumber);
  const rights = useContentField("footer.rights", footer.rights);
  const privacy = useContentField("footer.privacy", footer.privacy);
  const terms = useContentField("footer.terms", footer.terms);
  const whatsappHref = `https://wa.me/${whatsappNumber.replace(/[^0-9]/g, "")}`;

  return (
    <footer data-testid="site-footer" className="bg-motor-ink pt-14 sm:pt-16 pb-24 sm:pb-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10">
          <div>
            <a
              href={domainUrl}
              data-testid="footer-domain-link"
              className="inline-flex bg-white rounded-xl px-3 py-2 hover:opacity-90 transition-opacity"
            >
              <img src={logoSrc} alt="MotorPRO Autos" className="h-9 w-auto object-contain" />
            </a>
            <a
              href={domainUrl}
              data-testid="footer-domain-text"
              className="mt-4 block text-sm text-white/60 hover:text-white transition-colors"
            >
              {domain}
            </a>
          </div>

          <div className={colClass}>
            <h4 className="font-display font-semibold text-white text-sm uppercase tracking-wide">
              Síguenos
            </h4>
            <div className="mt-4 flex flex-col gap-3 text-sm text-white/70">
              <a
                href={links.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook-link"
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <Facebook className="h-4 w-4" /> {facebookHandle}
              </a>
              <a
                href={links.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <Instagram className="h-4 w-4" /> {instagramHandle}
              </a>
              <a
                href={links.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-tiktok-link"
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <TikTokIcon className="h-4 w-4" /> {tiktokHandle}
              </a>
            </div>
          </div>

          <div className={colClass}>
            <h4 className="font-display font-semibold text-white text-sm uppercase tracking-wide">
              Contacto
            </h4>
            <a
              href={whatsappHref}
              onClick={() => trackContact("footer_whatsapp")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-link"
              className="mt-4 flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
            >
              <MessageCircle className="h-4 w-4" /> {whatsappNumber}
            </a>
          </div>

          <div className={colClass}>
            <h4 className="font-display font-semibold text-white text-sm uppercase tracking-wide">
              Legal
            </h4>
            <div className="mt-4 flex flex-col gap-3 text-sm text-white/70">
              <a
                href={links.privacyPolicy}
                data-testid="footer-privacy-link"
                className="hover:text-white transition-colors"
              >
                {privacy}
              </a>
              <a
                href={links.termsAndConditions}
                data-testid="footer-terms-link"
                className="hover:text-white transition-colors"
              >
                {terms}
              </a>
            </div>
          </div>
        </div>

        <div
          data-testid="footer-copyright"
          className="mt-12 pt-6 border-t border-white/10 text-center text-xs text-white/40"
        >
          {rights}
        </div>
      </div>
    </footer>
  );
};
